import { pathEq, complement, curry, reject, uniq } from 'ramda';
import actionTypes from '../actions/reduxActions/actionTypes';

// invitation example:
// {
//     "completedAt": "",
//     "completedBy": {
//       "email": "",
//       "firstName": "",
//       "id": "",
//       "lastName": ""
//     },
//     "invitedAt": "",
//     "invitedBy": {
//       "email": "",
//       "firstName": "",
//       "id": "",
//       "lastName": ""
//     },
//     "organization": {
//       "id": "",
//       "name": ""
//     },
//     "status": "accepted" | "rejected",
//     "tenant": {
//       "developerName": "",
//       "id": ""
//     },
//     "type": "tenant" | "user",
//     "user": null
// }
// Only tenant or user property will be populated

/**
 *
 * @description Checks if an invite was sent to the supplied user or tenant
 * @param {String} userId
 * @param {String} tenantId
 * @param {Object} invite
 * @returns {Boolean}
 */
export const isReceivedInvite = curry((userId, tenantId, invite) => {

    // invitation is for current user
    const matchingUser = pathEq(['user', 'id'], userId, invite);
    // invitation is for current tenant
    const matchingTenant = pathEq(['tenant', 'id'], tenantId, invite);

    return matchingTenant || matchingUser;
});

/**
 *
 * @description Checks if an invite was not sent to the supplied user or tenant
 * (All other (non-received) invites must have been sent by an admin of the current organization)
 * @param {String} userId
 * @param {String} tenantId
 * @param {Object} invite
 * @returns {Boolean}
 */
export const isSentInvite = complement(isReceivedInvite);

const replaceSentInvitationsByInviteType = ({
    inviteType,
    userId, 
    tenantId,
    sentInvitations,
    currentInvitations,
}) => {

    // remove current sent <tenant|user> invitations
    const filteredInvitations = reject(
        invite => isSentInvite(userId, tenantId, invite) && invite.type === inviteType,
        currentInvitations,
    );

    // sent invitations do not have types set by the API, so set it here
    const newInvitations = sentInvitations.map(invite => ({
        ...invite,
        type: invite.type || inviteType,
    }));

    // append sent <tenant|user> invitations from payload
    // ensure there are no duplicates because
    // different API endpoints can return the same invitation
    const mergedInvites = uniq([
        ...filteredInvitations,
        ...newInvitations,
    ]);

    return mergedInvites;
};

const organizationInvitations = (state = [], { type, payload }) => {

    switch (type) {

    case actionTypes.ORGANIZATION_SET_RECEIVED_INVITATIONS: {

        // payload includes:
        // current user id
        // current tenant id
        // all received invitations for the current user or tenant

        // filter out any current received invitations
        const filteredInvitations = reject(
            isReceivedInvite(payload.userId, payload.tenantId),
            state,
        );

        // append received invitations from payload
        return uniq([
            ...filteredInvitations,
            ...payload.receivedInvitations,
        ]);
    }

    case actionTypes.ORGANIZATION_SET_SENT_TENANT_INVITATIONS: {

        // payload includes:
        // current user id
        // current tenant id
        // all invitations sent to the current tenant

        return replaceSentInvitationsByInviteType({
            inviteType: 'tenant',
            userId: payload.userId, 
            tenantId: payload.tenantId,
            sentInvitations: payload.sentInvitations,
            currentInvitations: state,
        });
    }        

    case actionTypes.ORGANIZATION_SET_SENT_USER_INVITATIONS: {

        // payload includes:
        // current user id
        // current tenant id
        // all invitations sent to the current user

        return replaceSentInvitationsByInviteType({
            inviteType: 'user',
            userId: payload.userId, 
            tenantId: payload.tenantId,
            sentInvitations: payload.sentInvitations,
            currentInvitations: state,
        });
    }        

    default:
        return state;
    }
};

export default organizationInvitations;
